import React, { useState, useEffect } from 'react';
import { Button, Form } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import TaskTable from './TaskTable';
import TaskModal from './TaskModal';
import { useTranslationApi } from './useTranslationApi';
import { TextTranslationProps } from './types';
import { Task } from './types';

const TextTranslation: React.FC<TextTranslationProps> = ({ username, isLoggedIn }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | undefined>();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const {
    tasks,
    total,
    page,
    pageSize,
    loading,
    submitLoading,
    fetchTasks,
    handleDownload,
    handleRetry,
    handleEditRetry,
    handleSubmit
  } = useTranslationApi(username);

  useEffect(() => {
    if (isLoggedIn && username) {
      fetchTasks();
    }
  }, [isLoggedIn, username, fetchTasks]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    fetchTasks(newPage, newPageSize);
  };

  const handleCreateTask = () => {
    setModalOpen(true);
  };

  const handleRefresh = () => {
    fetchTasks(page, pageSize);
  };

  const handleModalSubmit = async () => {
    const values = await form.validateFields();
    console.log('表单数据:', values);
    
    const formData = new FormData();
    
    // 处理文件上传
    if (values.file && values.file.length > 0) {
      formData.append('file', values.file[0].originFileObj);
    }
    
    // 处理其他字段
    formData.append('task_type', values.task_type);
    formData.append('target_language', values.target_language);
    
    if (values.translate_columns) {
      formData.append('translate_columns', values.translate_columns);
    }
    
    if (values.custom_columns) {
      formData.append('custom_columns', values.custom_columns);
    }
    
    const success = await handleSubmit(formData);
    if (success) {
      setModalOpen(false);
      form.resetFields();
    }
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const handleEditRetryClick = (task: Task) => {
    setEditingTask(task);
    setEditModalOpen(true);
  };

  const handleEditModalSubmit = async () => {
    const values = await editForm.validateFields();
    console.log('编辑表单数据:', values);
    
    if (!editingTask) return;
    
    const formData = new FormData();
    
    // 处理其他字段
    formData.append('task_type', values.task_type);
    formData.append('target_language', values.target_language);
    
    if (values.translate_columns) {
      formData.append('translate_columns', values.translate_columns);
    }
    
    if (values.custom_columns) {
      formData.append('custom_columns', values.custom_columns);
    }
    
    const success = await handleEditRetry(editingTask.id, formData);
    if (success) {
      setEditModalOpen(false);
      setEditingTask(undefined);
      editForm.resetFields();
    }
  };

  const handleEditModalCancel = () => {
    setEditModalOpen(false);
    setEditingTask(undefined);
    editForm.resetFields();
  };

  if (!isLoggedIn) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>请先登录</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateTask}>
          新增文本智能翻译任务
        </Button>
        <Button icon={<ReloadOutlined />} onClick={handleRefresh}>刷新数据</Button>
      </div>
      
      <TaskTable
        tasks={tasks}
        loading={loading}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={handlePageChange}
        onDownload={handleDownload}
        onRetry={handleRetry}
        onEditRetry={handleEditRetryClick}
      />
      
      <TaskModal
        open={modalOpen}
        loading={submitLoading}
        form={form}
        onSubmit={handleModalSubmit}
        onCancel={handleModalCancel}
        isEditMode={false}
      />
      
      <TaskModal
        open={editModalOpen}
        loading={submitLoading}
        form={editForm}
        onSubmit={handleEditModalSubmit}
        onCancel={handleEditModalCancel}
        isEditMode={true}
        editTask={editingTask}
      />
    </div>
  );
};

export default TextTranslation;
